import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper,
  Button,
  Box
} from '@mui/material'

export interface FilterOption {
  value: string
  label: string
}

export interface FilterConfig {
  id: string
  label: string
  type: 'select' | 'date' | 'daterange'
  options?: FilterOption[]
  value?: string | { from: string; to: string }
  onChange: (value: any) => void
}

interface ExportFiltersProps {
  title: string
  filters: FilterConfig[]
}

function ExportFilters({ title, filters }: ExportFiltersProps) {
  if (filters.length === 0) {
    return null
  }

  const renderFilter = (filter: FilterConfig) => {
    switch (filter.type) {
      case 'select':
        return (
          <FormControl fullWidth size="small" key={filter.id}>
            <InputLabel>{filter.label}</InputLabel>
            <Select
              value={filter.value || ''}
              label={filter.label}
              onChange={(e) => filter.onChange(e.target.value)}
            >
              {filter.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )

      case 'date':
        return (
          <TextField
            key={filter.id}
            fullWidth
            label={filter.label}
            type="date"
            size="small"
            value={filter.value || ''}
            onChange={(e) => filter.onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        )

      case 'daterange':
        const dateValue = filter.value as { from: string; to: string } || { from: '', to: '' }
        return (
          <Grid container spacing={2} key={filter.id}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  size="small"
                  value={dateValue.from}
                  onChange={(e) => filter.onChange({ ...dateValue, from: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    placeholder: ' '  // Single space to hide dd/mm/yyyy text
                  }}
                />
                {dateValue.from && (
                  <Button
                    size="small"
                    variant="text"
                    color="secondary"
                    sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                    onClick={() => filter.onChange({ ...dateValue, from: '' })}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  size="small"
                  value={dateValue.to}
                  onChange={(e) => filter.onChange({ ...dateValue, to: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    placeholder: ' '  // Single space to hide dd/mm/yyyy text
                  }}
                />
                {dateValue.to && (
                  <Button
                    size="small"
                    variant="text"
                    color="secondary"
                    sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                    onClick={() => filter.onChange({ ...dateValue, to: '' })}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        )

      default:
        return null
    }
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        🎯 {title}
      </Typography>
      <Grid container spacing={2}>
        {filters.map((filter) => (
          <Grid 
            item 
            xs={12} 
            sm={filter.type === 'daterange' ? 12 : 6} 
            md={filter.type === 'daterange' ? 8 : 4} 
            key={filter.id}
          >
            {renderFilter(filter)}
          </Grid>
        ))}
      </Grid>
    </Paper>
  )
}

export default ExportFilters